import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { useMe } from "../../lib/me";
import { Button, ErrorText, PageHeader, Section, StatusText, TextField } from "../../ui";
import { newRequestId } from "../tasks/use-task-actions";
import { checkName } from "./profile-model";
import { SettingsNav } from "./SettingsNav";

/**
 * Settings → Profile (WP-029): the name other people see in chat, mentions and member lists.
 * Saved through `people.updateProfile`; the email is the Google sign-in and cannot change here.
 */
export function ProfilePage() {
  const me = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [name, setName] = useState(me.name);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const save = useMutation(
    trpc.people.updateProfile.mutationOptions({
      onSuccess: async (_out, vars) => {
        setSaved(vars.input.name);
        setName(vars.input.name);
        // The top bar reads `me`; member lists here are queries too.
        await queryClient.invalidateQueries({ queryKey: trpc.me.queryKey() });
        await queryClient.invalidateQueries({ queryKey: trpc.workspaces.members.queryKey() });
      },
    }),
  );

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaved(null);
    const checked = checkName(name);
    if (!checked.ok) {
      setInvalid(checked.message);
      return;
    }
    setInvalid(null);
    save.mutate({ requestId: newRequestId(), input: { name: checked.name } });
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Profile" />
      <SettingsNav />
      <Section title="Your profile" description="Your name is what other people see in chat, mentions and member lists.">
        <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end" noValidate>
          <TextField
            label="Name"
            name="name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={invalid !== null}
            className="flex-1"
          />
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </form>
        <div className="mt-2 min-h-5">
          {invalid === null ? null : <ErrorText>{invalid}</ErrorText>}
          {save.isError ? <ErrorText>{errorMessage(save.error)}</ErrorText> : null}
          {saved === null ? null : <StatusText>Saved. People now see you as {saved}.</StatusText>}
        </div>
        <TextField
          label="Email"
          name="email"
          value={me.email}
          readOnly
          hint="Your Google sign-in address; it cannot be changed here."
          className="mt-4"
        />
      </Section>
    </div>
  );
}
